import React, { useEffect, useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams, useLocation, useNavigate } from 'react-router-dom';
import './App.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import GuestServices from './pages/GuestServices';
import GuestExplore from './pages/GuestExplore';
import GuestFood from './pages/GuestFood';
import GuestOrders from './pages/GuestOrders';
import GuestHeader from './components/GuestHeader';
import GuestOtp from './pages/GuestOtp';
import GuestOtpVerification from './pages/GuestOtpVerification';
import GuestError from './pages/GuestError';
import { CartProvider } from './components/CartContext';
import GuestAmenities from './pages/GuestAmenities';
import GuestNearby from './pages/GuestNearby';
import { API_ENDPOINTS } from './config/api';
import { useHotelTitle } from './hooks/useHotelTitle';

// GuestContext for session info
export interface GuestSession {
  mobile: string;
  guest_id: string;
}
export const GuestContext = createContext<GuestSession | null>(null);
export const useGuest = () => useContext(GuestContext);

// TokenContext for parsed hotel/room info
interface TokenContextType {
  hotelId: string;
  roomId: string;
  token: string;
}
const TokenContext = createContext<TokenContextType | null>(null);
export const useToken = () => useContext(TokenContext);

// Interface for parsed token data
interface TokenData {
  hotel_id: number;
  room_id: number;
}

// Function to parse base64 token
const parseToken = (token: string): TokenData | null => {
  try {
    const decoded = atob(token);
    const data = JSON.parse(decoded);
    
    if (data.hotel_id && data.room_id) {
      return {
        hotel_id: parseInt(data.hotel_id),
        room_id: parseInt(data.room_id)
      };
    }
    return null;
  } catch (error) {
    return null;
  }
};

const BottomTabNav: React.FC = () => {
  const { token } = useToken()!;
  const location = useLocation();
  const isActive = (tab: string) => location.pathname.includes(`/${tab}`);
  const navigate = useNavigate();
  return (
    <nav className="navbar navbar-light bg-white shadow-lg fixed-bottom border-top" style={{ height: 80, zIndex: 1050 }}>
      <div className="container d-flex justify-content-around align-items-center h-100">
        <button className={`btn btn-link flex-fill text-center tab-btn${isActive('explore') ? ' active' : ''}`} id="tab-btn-explore" onClick={() => navigate(`/${token}/explore`)}>
          <i className="fas fa-compass fa-lg"></i><br /><span style={{ fontSize: 12 }}>Explore</span>
        </button>
        <button className={`btn btn-link flex-fill text-center tab-btn${isActive('services') ? ' active' : ''}`} id="tab-btn-dialer" onClick={() => navigate(`/${token}/services`)}>
          <i className="fas fa-phone fa-lg"></i><br /><span style={{ fontSize: 12 }}>Services</span>
        </button>
        <button className={`btn btn-link flex-fill text-center tab-btn${isActive('food') ? ' active' : ''}`} id="tab-btn-food" onClick={() => navigate(`/${token}/food`)}>
          <i className="fas fa-utensils fa-lg"></i><br /><span style={{ fontSize: 12 }}>Food</span>
        </button>
        <button className={`btn btn-link flex-fill text-center tab-btn${isActive('orders') ? ' active' : ''}`} id="tab-btn-orders" onClick={() => navigate(`/${token}/orders`)}>
          <i className="fas fa-receipt fa-lg"></i><br /><span style={{ fontSize: 12 }}>My Orders</span>
        </button>
      </div>
    </nav>
  );
};

const GuestTabs: React.FC = () => {
  const location = useLocation();
  const { hotelName } = useHotelTitle();
  
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.pathname]);
  
  return (
    <>
      <GuestHeader />
      <Routes>
        <Route path="services" element={<GuestServices />} />
        <Route path="amenities" element={<GuestAmenities />} />
        <Route path="nearby" element={<GuestNearby />} />
        <Route path="explore" element={<GuestExplore />} />
        <Route path="food" element={<GuestFood />} />
        <Route path="orders" element={<GuestOrders />} />
        <Route path="*" element={<Navigate to="explore" replace />} />
      </Routes>
      <BottomTabNav />
    </>
  );
};

const GUEST_SESSION_DURATION_MS = 60 * 60 * 1000; // 60 minutes

interface StoredGuestSession extends GuestSession {
  sessionStartedAt: number;
}

const getStoredGuest = (): GuestSession | null => {
  const raw = localStorage.getItem('guestSession');
  if (!raw) return null;
  try {
    const stored: StoredGuestSession = JSON.parse(raw);
    if (!stored.mobile || !stored.guest_id || typeof stored.sessionStartedAt !== 'number') return null;
    const elapsed = Date.now() - stored.sessionStartedAt;
    if (elapsed >= GUEST_SESSION_DURATION_MS) {
      localStorage.removeItem('guestSession');
      return null;
    }
    return { mobile: stored.mobile, guest_id: stored.guest_id };
  } catch {
    return null;
  }
};

const GuestAuthWrapper: React.FC = () => {
  const { token } = useToken()!;
  const [guest, setGuest] = useState<GuestSession | null>(() => getStoredGuest());
  const navigate = useNavigate();

  const handleOtpSuccess = (mobile: string, guestId: string) => {
    const session: GuestSession = { mobile, guest_id: guestId };
    const toStore: StoredGuestSession = { ...session, sessionStartedAt: Date.now() };
    setGuest(session);
    localStorage.setItem('guestSession', JSON.stringify(toStore));
    navigate(`/${token}/explore`, { replace: true });
  };

  if (!guest) {
    return <GuestOtp onSuccess={handleOtpSuccess} />;
  }
  return <GuestContext.Provider value={guest}><GuestTabs /></GuestContext.Provider>;
};

// Component to handle token parsing and provide context
const GuestTokenProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { token: tokenParam } = useParams<{ token?: string }>();
  const token = tokenParam || '';
  const navigate = useNavigate();
  useEffect(() => {
    if (!token) {
      navigate('/error', { replace: true });
      return;
    }
    const tokenData = parseToken(token);
    if (!tokenData) {
      navigate('/error', { replace: true });
      return;
    }
  }, [token, navigate]);
  const tokenData = token ? parseToken(token) : null;
  if (!tokenData) return null;
  return (
    <TokenContext.Provider value={{ hotelId: String(tokenData.hotel_id), roomId: String(tokenData.room_id), token }}>
      {children}
    </TokenContext.Provider>
  );
};

const App: React.FC = () => (
  <CartProvider>
    <Router>
      <Routes>
        <Route path="/:token/*" element={
          <GuestTokenProvider>
            <Routes>
              <Route path="/*" element={<GuestAuthWrapper />} />
            </Routes>
          </GuestTokenProvider>
        } />
        <Route path="/otp" element={<GuestOtpVerification />} />
        <Route path="/error" element={<GuestError />} />
        <Route path="*" element={<Navigate to="/error" replace />} />
      </Routes>
    </Router>
  </CartProvider>
);

export default App;
